import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet,
  TrendingUp,
  Clock,
  CheckCircle2,
  RefreshCw,
  Building2,
  User,
  Banknote,
  AlertCircle,
} from 'lucide-react';
import { CourierWalletAdminReport } from '../../types';
import {
  fetchAdminCourierWalletReport,
  resolveOutletDisplayName,
} from '../../utils/courierWallet';

export const AdminCourierWalletReport: React.FC = () => {
  const [report, setReport] = useState<CourierWalletAdminReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'WITHDRAWALS' | 'WALLETS' | 'EARNINGS'>('WITHDRAWALS');

  const loadReport = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await fetchAdminCourierWalletReport();
      setReport(data);
      setErrorMsg(null);
    } catch (err: any) {
      if (!silent) {
        setErrorMsg(err?.message || 'Gagal memuat laporan Courier Wallet.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadReport(false);
    const timer = setInterval(() => loadReport(true), 15000);
    return () => clearInterval(timer);
  }, [loadReport]);

  const formatRupiah = (amount: number) => `Rp${Number(amount || 0).toLocaleString('id-ID')}`;

  const formatDateTime = (iso?: string | null) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const totals = report?.totals || {
    totalEarned: 0,
    totalAvailableBalance: 0,
    totalProcessingBalance: 0,
    totalWithdrawn: 0,
    pendingWithdrawalCount: 0,
    paidWithdrawalCount: 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#0A5C99] text-xs font-extrabold uppercase tracking-wider">
            <Wallet className="w-3.5 h-3.5" />
            <span>Super Admin • Laporan Courier Wallet</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 mt-2">
            Rekonsiliasi Penghasilan & Pencairan Kurir LetonKu
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 mt-1">
            Pantau total penghasilan ongkir kurir (100%), saldo tersedia, saldo sedang diproses, serta riwayat outlet & kasir pembayar.
          </p>
        </div>

        <button
          type="button"
          onClick={() => loadReport(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-colors self-start sm:self-auto"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-[#0A5C99]' : ''}`} />
          <span>Segarkan Laporan</span>
        </button>
      </div>

      {errorMsg && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-center gap-3 text-rose-900 text-xs sm:text-sm font-semibold">
          <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* 4 Executive Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase">Total Penghasilan Kurir</span>
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-[#0A5C99] flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-slate-900 mt-3">{formatRupiah(totals.totalEarned)}</p>
          <p className="text-[11px] text-slate-500 mt-1">Akumulasi 100% ongkir order selesai & lunas</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase">Total Saldo Tersedia</span>
            <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <Wallet className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-emerald-700 mt-3">
            {formatRupiah(totals.totalAvailableBalance)}
          </p>
          <p className="text-[11px] text-slate-500 mt-1">Saldo aktif di dompet seluruh kurir</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase">Saldo Sedang Diproses</span>
            <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-amber-700 mt-3">
            {formatRupiah(totals.totalProcessingBalance)}
          </p>
          <p className="text-[11px] text-slate-500 mt-1">
            {totals.pendingWithdrawalCount} pengajuan menunggu kasir
          </p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase">Total Pencairan (PAID)</span>
            <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-slate-900 mt-3">
            {formatRupiah(totals.totalWithdrawn)}
          </p>
          <p className="text-[11px] text-slate-500 mt-1">
            {totals.paidWithdrawalCount} transaksi telah dibayar kasir
          </p>
        </div>
      </div>

      {/* Tabs: Riwayat Pencairan, Saldo Per Kurir, Riwayat Penghasilan Order */}
      <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xs overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 pt-3 overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveTab('WITHDRAWALS')}
            className={`px-4 py-3 text-xs font-extrabold uppercase tracking-wider border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'WITHDRAWALS'
                ? 'border-[#0A5C99] text-[#0A5C99]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Riwayat Pencairan & Audit Kasir ({report?.withdrawals.length || 0})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('WALLETS')}
            className={`px-4 py-3 text-xs font-extrabold uppercase tracking-wider border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'WALLETS'
                ? 'border-[#0A5C99] text-[#0A5C99]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Daftar Dompet Kurir ({report?.wallets.length || 0})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('EARNINGS')}
            className={`px-4 py-3 text-xs font-extrabold uppercase tracking-wider border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'EARNINGS'
                ? 'border-[#0A5C99] text-[#0A5C99]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Riwayat Penghasilan Ongkir ({report?.earnings.length || 0})
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Memuat data laporan...</div>
        ) : activeTab === 'WITHDRAWALS' ? (
          !report || report.withdrawals.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Belum ada riwayat pencairan saldo kurir.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                    <th className="py-3.5 px-4">ID Transaksi</th>
                    <th className="py-3.5 px-4">Kurir</th>
                    <th className="py-3.5 px-4">Nominal</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-4">Outlet Pembayaran</th>
                    <th className="py-3.5 px-4">Kasir yang Membayar</th>
                    <th className="py-3.5 px-4">Waktu Pengajuan / Bayar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {report.withdrawals.map((wd) => (
                    <tr key={wd.id} className="hover:bg-slate-50/70">
                      <td className="py-3.5 px-4 font-mono font-black text-[#0A5C99]">
                        {wd.transactionCode}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-900">{wd.courierName}</div>
                        <div className="text-slate-500">@{wd.courierUsername}</div>
                      </td>
                      <td className="py-3.5 px-4 font-black text-slate-900">
                        {formatRupiah(wd.amount)}
                      </td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase ${
                            wd.status === 'PAID'
                              ? 'bg-emerald-100 text-emerald-800'
                              : wd.status === 'PENDING'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-rose-100 text-rose-800'
                          }`}
                        >
                          {wd.status === 'PAID'
                            ? 'PAID (DIBAYAR)'
                            : wd.status === 'PENDING'
                            ? 'MENUNGGU PEMBAYARAN'
                            : 'DIBATALKAN'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        {wd.status === 'PAID' ? (
                          <span className="font-bold text-slate-900 inline-flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5 text-emerald-600" />
                            {wd.paidAtOutletName || resolveOutletDisplayName(wd.paidAtOutletId)}
                          </span>
                        ) : (
                          <span className="text-slate-500">
                            Target: {resolveOutletDisplayName(wd.targetOutletId || 'ANY')}
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-4">
                        {wd.status === 'PAID' ? (
                          <span className="font-bold text-emerald-700 inline-flex items-center gap-1">
                            <User className="w-3.5 h-3.5" />
                            {wd.paidByCashierName || wd.paidByCashierUsername}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-slate-600">
                        <div>Pengajuan: {formatDateTime(wd.requestedAt)}</div>
                        {wd.paidAt && (
                          <div className="text-emerald-700 font-semibold">
                            Dibayar: {formatDateTime(wd.paidAt)}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : activeTab === 'WALLETS' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                  <th className="py-3.5 px-4">Nama Kurir</th>
                  <th className="py-3.5 px-4">Cabang Outlet</th>
                  <th className="py-3.5 px-4">Total Penghasilan</th>
                  <th className="py-3.5 px-4">Saldo Tersedia</th>
                  <th className="py-3.5 px-4">Sedang Diproses</th>
                  <th className="py-3.5 px-4">Total Dicairkan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {(report?.wallets || []).map((w) => (
                  <tr key={w.courierUsername} className="hover:bg-slate-50/70">
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-slate-900">{w.courierName}</div>
                      <div className="text-slate-500 font-mono">@{w.courierUsername}</div>
                    </td>
                    <td className="py-3.5 px-4 font-semibold text-slate-700">
                      {resolveOutletDisplayName(w.outletId)}
                    </td>
                    <td className="py-3.5 px-4 font-bold text-slate-900">
                      {formatRupiah(w.totalEarned)}
                    </td>
                    <td className="py-3.5 px-4 font-black text-emerald-700">
                      {formatRupiah(w.availableBalance)}
                    </td>
                    <td className="py-3.5 px-4 font-bold text-amber-700">
                      {formatRupiah(w.processingBalance)}
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#0A5C99]">
                      {formatRupiah(w.totalWithdrawn)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-[11px] font-extrabold uppercase tracking-wider text-slate-500">
                  <th className="py-3.5 px-4">Order</th>
                  <th className="py-3.5 px-4">Kurir</th>
                  <th className="py-3.5 px-4">Ongkir (100%)</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4">Waktu Dicatat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {(report?.earnings || []).map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/70">
                    <td className="py-3.5 px-4 font-black text-slate-900">
                      #{e.orderNumber.replace(/^#/, '')}
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-slate-900">{e.courierName}</div>
                      <div className="text-slate-500">@{e.courierUsername}</div>
                    </td>
                    <td className="py-3.5 px-4 font-black text-emerald-700">
                      {formatRupiah(e.amount)}
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                          e.status === 'CREDITED'
                            ? 'bg-emerald-100 text-emerald-800'
                            : e.status === 'REVERSED'
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {e.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-slate-600">{formatDateTime(e.earnedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
